/**
 * Module dependencies.
 */

import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { useDrawer } from 'app/components/organisms/drawer/drawer-context';
import { cn } from 'app/lib/utils';
import React, { ComponentType, useEffect, useMemo, useRef } from 'react';
import { FieldValues, SubmitHandler, UseFormReturn, useWatch } from 'react-hook-form';
import { FormDatePickerField } from './form-components/form-date-picker-field';
import { FormInputField } from './form-components/form-input-field';
import { FormInputNumber } from './form-components/form-input-number';
import { FormSelectField } from './form-components/form-select-field';
import { FormCardSelectField } from './form-components/form-card-select-field';
import { FormFieldType, FormList, FormListField } from './form-types';
import { IfCondition } from './form-widgets/if-condition';
import { Render } from './form-widgets/render';
import { FormCheckBoxField } from './form-components/form-checkbox-field';
import { SwitchCondition } from './form-widgets/switch-condition';
import { FormSliderField } from './form-components/form-slider-field';
import { Columns } from './form-widgets/columns';
import { ListItemForm } from './form-widgets/list-item-form';
import { LoadHooks } from './form-widgets/load-hooks';
import { FormInputCurrency } from './form-components/form-input-currency';

/**
 * `Props` type.
 */

type Props = {
  formType: 'add' | 'edit';
} & (
  | {
      onSubmit: SubmitHandler<FieldValues>;
      formFields: FormList;
      form: UseFormReturn<FieldValues, any>;
      submitLabel: string;
    }
  | {
      onSubmit?: undefined;
      submitLabel?: undefined;
      formFields: FormList;
      form: UseFormReturn<FieldValues, any>;
    }
);

/**
 * Components.
 */

export const components: Record<FormFieldType, ComponentType<any>> = {
  // Forms
  selectField: FormSelectField,
  cardSelectField: FormCardSelectField,
  checkBoxField: FormCheckBoxField,
  sliderField: FormSliderField,
  inputField: FormInputField,
  inputNumberField: FormInputNumber,
  inputCurrencyField: FormInputCurrency,
  render: Render,
  datePickerField: FormDatePickerField,

  // Widgets
  switch: SwitchCondition,
  if: IfCondition,
  loadHooks: LoadHooks,
  columns: Columns,
  listItemForm: ListItemForm
};

/**
 * `FormProps` type.
 */

type FormProps = {
  form: Props['form'];
  onSubmit?: SubmitHandler<FieldValues>;
  submitLabel: Props['submitLabel'];
  children: JSX.Element;
};

/**
 * `Form` component.
 */

export function Form({ form, onSubmit, children, submitLabel }: FormProps): JSX.Element {
  const {
    handleSubmit,
    formState: { isSubmitting, isDirty }
  } = form;
  const { inDrawer, registerDirtyCheck } = useDrawer();

  // Let the drawer warn about unsaved changes on close. A submitting form is
  // never "dirty" for this purpose (it is being saved, not abandoned).
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty && !isSubmitting;

  useEffect(() => {
    if (!inDrawer) {
      return;
    }

    registerDirtyCheck(() => isDirtyRef.current);

    return () => registerDirtyCheck(null);
  }, [inDrawer, registerDirtyCheck]);

  if (!onSubmit || !submitLabel) {
    return children;
  }

  return (
    <form className={cn(inDrawer && 'flex min-h-full flex-col')} onSubmit={handleSubmit(onSubmit)}>
      {inDrawer ? <div className={'flex-1'}>{children}</div> : children}

      {/* Inside a drawer the button bar sticks to the bottom of the scroll area
          (spanning the body's padding) so it stays visible on long forms. */}
      <SubmitButton
        className={cn(
          inDrawer && 'sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end border-t border-border bg-background px-6 py-4'
        )}
        disabled={isSubmitting}
        label={submitLabel}
      />
    </form>
  );
}

/**
 * Export `CrudFormRenderProps` type.
 */

export type CrudFormRenderProps = {
  formFields: FormList;
  formType: 'add' | 'edit';
  form: UseFormReturn<FieldValues, any>;
};

/**
 * `CrudFormItemRenderProps` type.
 */

type CrudFormItemRenderProps = Pick<CrudFormRenderProps, 'formType' | 'form'> & {
  item: FormListField;
  values: any;
};

/**
 * `CrudFormItemRender` component.
 */

function CrudFormItemRender(props: CrudFormItemRenderProps): JSX.Element | null {
  const { form, formType, item, values } = props;
  const Component = components[item.type];
  const { watchChange } = item as any;
  const firstWatch = useRef<boolean>(true);
  const lastValueRef = useRef<any>(null);
  const watchValues = useWatch({ control: form.control, name: watchChange?.watchFields ?? [] });
  const hasDisabled = useMemo(() => {
    return typeof (item as any).disabled === 'function' ? (item as any).disabled(values ?? {}) : (item as any).disabled;
  }, [item, values]);

  useEffect(() => {
    if (!watchChange || firstWatch.current) {
      firstWatch.current = false;

      return;
    }

    if (JSON.stringify(lastValueRef.current) === JSON.stringify(watchValues)) {
      return;
    }

    lastValueRef.current = watchValues;

    watchChange.onChange(watchValues, form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, watchValues]);

  if ((item as any).visible && (item as any).visible(values) === false) {
    return null;
  }

  return (
    <Component
      form={form}
      formType={formType}
      item={{
        ...item,
        disabled: hasDisabled
      }}
      key={(item as any).key ?? (item as any).name}
    />
  );
}

/**
 * `CrudFormRender` component.
 */

export function CrudFormRender({ form, formType, formFields }: CrudFormRenderProps): JSX.Element {
  const values = form.watch();

  return (
    <>
      {formFields.map(item => {
        return (
          <CrudFormItemRender
            form={form}
            formType={formType}
            item={item}
            key={(item as any).key ?? (item as any).name}
            values={values}
          />
        );
      })}
    </>
  );
}

/**
 * Export `CrudForm` component.
 */

export function CrudForm({ onSubmit, formType, submitLabel, form, formFields }: Props): JSX.Element {
  return (
    <Form form={form} onSubmit={onSubmit} submitLabel={submitLabel}>
      <CrudFormRender form={form} formFields={formFields} formType={formType} />
    </Form>
  );
}
